import { useEffect } from 'react';
import { CounterpartyService } from 'api';
import { BaseParams } from 'api/api.types';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { Counterparty, InvoiceClass } from 'pages/invoices/invoices.types';
import { FieldPath, FieldValues } from 'react-hook-form';
import { createHandleOnBlur, handleChange } from 'utils/form.utils';
import {
  isForeignInvoice,
  isInvoiceCorrective,
  isInvoiceInternal,
} from 'utils/invoiceFunctions.utils';
import { RenderProps } from './types';

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  formProps: RenderProps<TFieldValues, TName>;
  isEditing?: boolean;
  options: Counterparty[];
  params: Required<BaseParams>;
  setParams: React.Dispatch<React.SetStateAction<Required<BaseParams>>>;
  setOptions: React.Dispatch<React.SetStateAction<Counterparty[]>>;
  invoiceClassValue: InvoiceClass | null;
  error?: boolean;
  isDisabled?: boolean;
};

export default function CounterpartyAutocomplete<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  formProps,
  isEditing,
  invoiceClassValue,
  setOptions,
  setParams,
  options,
  params,
  error,
  isDisabled,
}: Props<TFieldValues, TName>) {
  const { getCounterparties, loading, counterpartiesData } =
    CounterpartyService.useGetCounterparties();

  useEffect(() => {
    if (counterpartiesData?.length) {
      const enabledCounterparties = counterpartiesData.filter(
        (counterparty) => counterparty.enabled,
      );
      setOptions((prev) => [...prev, ...enabledCounterparties]);
    }
  }, [counterpartiesData]);

  useEffect(() => {
    getCounterparties({ ...params });
  }, [getCounterparties, params]);

  return (
    <AutocompletePaginationAsync
      {...formProps}
      padding='dense'
      width={280}
      value={formProps.field.value}
      error={error}
      disabled={(() => {
        const isDisabledProp = !!isDisabled;
        const isCorrectiveNotEditing =
          !isEditing &&
          invoiceClassValue?.type &&
          isInvoiceCorrective(invoiceClassValue);
        const isCorrectiveEditing =
          !!isEditing &&
          !!invoiceClassValue &&
          !isForeignInvoice(invoiceClassValue) &&
          isInvoiceCorrective(invoiceClassValue);
        const isInternal =
          !!invoiceClassValue && isInvoiceInternal(invoiceClassValue);

        return (
          isDisabledProp ||
          isCorrectiveNotEditing ||
          isCorrectiveEditing ||
          isInternal
        );
      })()}
      loading={loading}
      getOptionLabel={(option) => (option?.code ? option.code : '')}
      options={options}
      handleSearchChange={handleChange(setOptions, setParams)}
      onScrollBottom={() =>
        setParams((prev) => ({
          ...prev,
          pageNumber: (prev.pageNumber += 1),
        }))
      }
      onBlur={createHandleOnBlur(setOptions, setParams, formProps.field.onBlur)}
      onChange={(_, data) => formProps.field.onChange(data)}
    />
  );
}
